

import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Auth0ProviderWithNavigate } from './utils/Auth0ProviderWithNavigate.js';
import { useSyncUserOnAuth } from './utils/useSyncUserOnAuth.js';
import { UserProvider, useUserSession } from './contexts/UserContext.js';
import { UserCacheProvider } from './contexts/UserCacheContext.js';
import { PendingOperationsProvider } from './contexts/PendingOperationsContext.js';
import ErrorBoundary from './components/ErrorBoundary.js';
import './App.css';
import './App.tablet.css';
import './App.mobile.css';
import Home from './components/Home.js';
import Landing from './components/Landing.js';
import NotFound from './components/NotFound.js';
import LocalDevPanel from './components/LocalDevPanel.js';
import OfflineBanner from './components/OfflineBanner.js';
import React, { useEffect, useState } from 'react';

const useAuth = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  return { user, isSignedIn: !!isAuthenticated, isLoaded: !isLoading };
};

// Component inside Auth0Provider to handle user sync
const AppWithAuth = () => {
  const { userSession, hasElevatedAccess } = useUserSession();
  let isSignedIn = false;
  
  // Check if we're on localhost for dev mode
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Call sync hook inside Auth0Provider context - captures sync status/errors
  const { syncStatus, syncError } = useSyncUserOnAuth();
  
  try {
    const authData = useAuth();
    isSignedIn = authData?.isSignedIn || false;
  } catch (error) {
    // Fallback to not signed in if auth fails
    isSignedIn = false;
  }

  // Check if user has elevated access (God or Immortal) OR has paid (for limited access)
  // On localhost, allow dev panel to override auth, but still respect normal auth for production behavior
  const hasSystemAccess = isLocalhost 
    ? (hasElevatedAccess() || (isSignedIn && (hasElevatedAccess() || userSession.hasPaid))) 
    : (isSignedIn && (hasElevatedAccess() || userSession.hasPaid));
  
  return (
    <>
      {syncError && (
        <div className="sync-error-banner" style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          Profile sync failed: {syncError}. Some features may not work correctly.
        </div>
      )}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/home" element={hasSystemAccess ? <Home /> : <Landing />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/onboarding" element={<Landing forceModalOpen={true} />} />
        <Route path="/register" element={<Landing />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* LocalDevPanel only on localhost - not staging */}
      {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && <LocalDevPanel />}
    </>
  );
};


const App = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineDetectedByFetch, setOfflineDetectedByFetch] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setOfflineDetectedByFetch(false);
    };
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Fallback: periodically check if fetch fails due to network
    let intervalId = setInterval(() => {
      fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' })
        .then(() => setOfflineDetectedByFetch(false))
        .catch(() => setOfflineDetectedByFetch(true));
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, []);

  const showOfflineBanner = isOffline || offlineDetectedByFetch;

  return (
    <ErrorBoundary>
      {showOfflineBanner && <OfflineBanner />}
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Auth0ProviderWithNavigate>
          <UserProvider>
            <UserCacheProvider>
              <PendingOperationsProvider>
                <AppWithAuth />
              </PendingOperationsProvider>
            </UserCacheProvider>
          </UserProvider>
        </Auth0ProviderWithNavigate>
      </Router>
    </ErrorBoundary>
  );
}

export default App;